import { faEdit, faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppContext from '../../../gateway/Authentication/AppContext'
import Authorization from '../../../gateway/Authentication/Authorization'
import AuthService from '../../../gateway/Authentication/AuthService'
import DialogContext from '../../../gateway/Authentication/DialogContext'
import { ConfirmDialogProps } from '../../../gateway/components/ConfirmDialog'
import DataTable from '../../../gateway/components/DataTable'
import { EditDialogProps } from '../../../gateway/components/EditDialog'
import { FormDataControl } from '../../../gateway/components/FormFields'
import ProtectedButton from '../../../gateway/components/ProtectedButton'
import Loading from '../../../gateway/layout/Loading'
import TokenProfileService from '../../../gateway/service/TokenProfileService'
import { expandRowFunction, formatBooleanColumn } from '../../../gateway/utils/DatatableUtil'
import RESTProtectionConfigurationService from '../services/RESTProtectionConfigurationService'

const RESTProtectionConfiguration: FC = (props: any) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const { userPermissions } = useContext(AppContext)
  const [restProtectionConfigurations, setRestProtectionConfigurations] = useState([] as any[])
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    async function loadData() {
      await RESTProtectionConfigurationService.getByAppId(setRestProtectionConfigurations, props.appId)
    }

    loadData().finally(() => setLoading(false))
  }, [props.appId])

  async function callAddFieldConfigurationAfterFetch(row: any) {
    const tokenProfiles = await TokenProfileService.getConfigurableTokenProfiles().then((profiles) =>
      profiles.filter((profile: any) => !profile.unique && profile.reversible && (profile.generic || row.usesNonGenericTokenProfiles))
    )

    addFieldConfiguration(t, editDialogPropsSetter, setRestProtectionConfigurations, tokenProfiles, row)
  }

  async function editFieldConfigurationAfterFetch(row: any, usesNonGenericTokenProfiles: boolean) {
    const tokenProfiles = await TokenProfileService.getConfigurableTokenProfiles().then((profiles) =>
      profiles.filter((profile: any) => !profile.unique && profile.reversible && (profile.generic || usesNonGenericTokenProfiles))
    )
    editFieldConfiguration(t, editDialogPropsSetter, setRestProtectionConfigurations, tokenProfiles, row)
  }

  const columns = [
    {
      text: t('restprotection.name'),
      dataField: 'name',
      sort: true,
    },
    {
      text: t('restprotection.endpoint'),
      dataField: 'endpoint',
      sort: true,
    },
    {
      text: t('restprotection.usesNonGenericTokenProfiles'),
      dataField: 'usesNonGenericTokenProfiles',
      sort: true,
      formatter: (cell: any, row: any) => {
        return formatBooleanColumn(cell)
      },
    },
    {
      text: t('restprotection.post'),
      dataField: 'postEnabled',
      hidden: true,
      sort: true,
      formatter: (cell: any, row: any) => {
        return formatBooleanColumn(cell)
      },
    },
    {
      text: t('restprotection.put'),
      dataField: 'putEnabled',
      hidden: true,
      sort: true,
      formatter: (cell: any, row: any) => {
        return formatBooleanColumn(cell)
      },
    },
    {
      hidden: !AuthService.hasPermission(userPermissions, Authorization.adapter.edit),
      dataField: 'df1',
      isDummyField: true,
      text: t('action'),
      headerAlign: 'center',
      formatter: (cellData: any, row: any) => {
        return (
          <>
            <ProtectedButton
              permissions={Authorization.adapter.edit}
              variant='link'
              className='text-decoration-none pr-2'
              tooltip={t('restprotection.addFieldConfiguration')}
              onClick={() => callAddFieldConfigurationAfterFetch(row)}>
              <FontAwesomeIcon icon={faPlus} />
            </ProtectedButton>
            <ProtectedButton
              variant='link'
              className='text-decoration-none pr-2'
              tooltip={t('edit')}
              onClick={() => editEndpointConfiguration(t, editDialogPropsSetter, setRestProtectionConfigurations, row)}>
              <FontAwesomeIcon icon={faEdit} />
            </ProtectedButton>
            <ProtectedButton
              variant='link'
              className='text-danger text-link text-decoration-none'
              tooltip={t('delete')}
              onClick={() => deleteEndpointConfiguration(t, confirmDialogPropsSetter, setRestProtectionConfigurations, row)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </ProtectedButton>
          </>
        )
      },
    },
  ]

  const expandRow = expandRowFunction(renderer, () => { })

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={restProtectionConfigurations}
        columns={columns}
        caption={t('restprotection.configurationHeader')}
        expandRow={expandRow}
        captionButtons={[
          {
            onClick: () => createEndpointConfiguration(t, editDialogPropsSetter, setRestProtectionConfigurations, props.appId),
            text: t('new'),
            permissions: Authorization.adapter.edit,
          },
        ]}
        searchPlaceholder={t('restprotection.searchPlaceholder')}
      />
    </Loading>
  )

  function renderer(row: any, rowIndex: any, ...args: any[]) {
    const data: any[] = row.configs
    const usesNonGenericTokenProfiles = row.usesNonGenericTokenProfiles

    const columns: any[] = [
      {
        text: t('restprotection.jsonPath'),
        dataField: 'jsonPath',
        sort: true,
      },
      {
        text: t('restprotection.method'),
        dataField: 'method',
        formatter: (cell: any, row: any) => {
          if (cell === t('testdata.tokenizerConfig.methods.tokenization')) {
            return `${cell} (${row.tokenProfileName})`
          } else {
            return cell
          }
        },
      },
      {
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-link text-decoration-none'
                tooltip={t('edit')}
                onClick={() => editFieldConfigurationAfterFetch(row, usesNonGenericTokenProfiles)}>
                <FontAwesomeIcon icon={faEdit} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-danger text-link text-decoration-none'
                tooltip={t('delete')}
                onClick={() => deleteFieldConfiguration(t, confirmDialogPropsSetter, setRestProtectionConfigurations, row)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]

    if (!data.length) {
      return <React.Fragment key={row.id} />
    }
    return (
      <React.Fragment key={row.id}>
        <DataTable
          data={data}
          columns={columns}
          searchable={false}
          striped={false}
          hover={false}
          condensed={true}
          noDataIndication={() => <span className='text-center'>{t('testdata.tokenizerConfig.noConfigFound')}</span>}
        />
      </React.Fragment>
    )
  }
}

function deleteFieldConfiguration(t: TFunction, confirmDialogPropsSetter: React.Dispatch<any>, setRestProtectionConfigurations: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('restprotection.deleteFieldHeader'),
    body: t('restprotection.deleteFieldBody', { path: row.jsonPath }),
    submitText: t('yes'),
    onSubmit: RESTProtectionConfigurationService.deleteFieldConfigurationFromEndpoint,
    onSubmitExtraArgs: [row, t, setRestProtectionConfigurations],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function editFieldConfiguration(
  t: TFunction,
  editDialogPropsSetter: any,
  setRestProtectionConfigurations: React.Dispatch<any>,
  tokenProfiles: any[],
  row: any
) {
  const encryptionMethods = [t('testdata.tokenizerConfig.methods.encryption'), t('testdata.tokenizerConfig.methods.tokenization')]

  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('restprotection.editFieldHeader'),
    onSubmit: RESTProtectionConfigurationService.editFieldConfigurationForEndpoint,
    onSubmitExtraArgs: [t, setRestProtectionConfigurations],
    data: (custom: string[]) => [
      {
        id: 'id',
        type: 'hidden',
        value: row.id,
      },
      {
        id: 'endpointProfile',
        type: 'hidden',
        value: row.endpointProfile,
      },
      {
        id: 'jsonPath',
        type: 'text',
        label: t('restprotection.jsonPath'),
        placeholder: t('restprotection.jsonPathPlaceholder'),
        value: row.jsonPath,
        required: true,
      },
      {
        id: 'method',
        type: 'select',
        render: true,
        options: encryptionMethods,
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('restprotection.method'),
        required: true,
      },
      ...getFinalField(tokenProfiles, custom[0], t, row.tokenProfileName),
    ],
    initCustom: [row.method],
    labelGridSize: 3,
  }
  editDialogPropsSetter(editDialogProps)
}

function addFieldConfiguration(t: TFunction, editDialogPropsSetter: any, setRestProtectionConfigurations: React.Dispatch<any>, tokenProfiles: any[], row: any) {
  const encryptionMethods = [t('testdata.tokenizerConfig.methods.encryption'), t('testdata.tokenizerConfig.methods.tokenization')]

  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('restprotection.createFieldHeader'),
    onSubmit: RESTProtectionConfigurationService.addFieldConfigurationToEndpoint,
    onSubmitExtraArgs: [t, setRestProtectionConfigurations],
    data: (custom: string[]) => [
      {
        id: 'endpointProfile',
        type: 'hidden',
        value: row.id,
      },
      {
        id: 'jsonPath',
        type: 'text',
        label: t('restprotection.jsonPath'),
        placeholder: t('restprotection.jsonPathPlaceholder'),
        required: true,
      },
      {
        id: 'method',
        type: 'select',
        render: true,
        options: encryptionMethods,
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('restprotection.method'),
        required: true,
      },
      ...getFinalField(tokenProfiles, custom[0], t),
    ],
    initCustom: [t('testdata.tokenizerConfig.methods.tokenization')],
    labelGridSize: 3,
  }
  editDialogPropsSetter(editDialogProps)
}

function getFinalField(tokenProfiles: any[], custom: string, t: TFunction, existingValue?: any): FormDataControl[] {
  if (custom === t('testdata.tokenizerConfig.methods.tokenization')) {
    return [
      {
        id: 'tokenProfileName',
        type: 'select',
        options: tokenProfiles.map((profile) => profile.name),
        value: existingValue ? existingValue : tokenProfiles[0].name,
        label: t('testdata.tokenizerConfig.tokenProfile'),
        required: true,
      },
    ]
  }
  return []
}

function createEndpointConfiguration(t: TFunction, editDialogPropsSetter: any, setRestProtectionConfigurations: React.Dispatch<any>, appId: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('restprotection.createHeader'),
    onSubmit: RESTProtectionConfigurationService.createEndpointConfiguration,
    onSubmitExtraArgs: [t, setRestProtectionConfigurations],
    data: (custom: any[]) => [
      {
        id: 'name',
        type: 'text',
        label: t('restprotection.name'),
        placeholder: t('restprotection.namePlaceholder'),
        required: true,
      },
      {
        id: 'endpoint',
        type: 'text',
        label: t('restprotection.endpoint'),
        placeholder: t('restprotection.endpointPlaceholder'),
        required: true,
      },
      {
        id: 'searchEndpoint',
        type: 'text',
        label: t('restprotection.searchEndpoint'),
        required: false,
        placeholder: t('restprotection.searchEndpointPlaceholder'),
      },
      {
        id: 'searchMetadataFields',
        type: 'textarea',
        label: t('restprotection.searchMetadata'),
        required: false,
        placeholder: t('restprotection.searchMetadataPlaceholder'),
      },
      {
        id: 'searchQueryParameter',
        type: 'text',
        label: t('restprotection.searchQueryParameter'),
        required: false,
        placeholder: t('restprotection.searchQueryParameterPlaceHolder'),
      },
      {
        id: 'appId',
        type: 'hidden',
        value: appId,
        render: false,
      },
      {
        groupLabel: t('restprotection.shouldDetokenizeRequest'),
        groupName: 'detokenizeRequest',
        type: 'switch',
        checkboxes: [
          {
            id: 'detokenizeRequest'
          },
        ],
      },
      {
        groupLabel: t('restprotection.shouldEnableFileEncryption'),
        groupName: 'encryptFiles',
        type: 'switch',
        checkboxes: [
          {
            id: 'encryptFiles'
          },
        ],
      },
      {
        groupLabel: t('restprotection.usesNonGenericTokenProfiles'),
        groupName: 'usesNonGenericTokenProfiles',
        type: 'switch',
        checkboxes: [
          {
            id: 'usesNonGenericTokenProfiles',
            onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e]),
            value: custom[0] === true,
          },
        ],
      },
      toggleAdditionalEndpointTextBox(custom[0] === true, t),
      {
        groupLabel: t('restprotection.methods'),
        groupName: 'methods',
        render: false,
        type: 'checkbox',
        checkboxes: [
          {
            id: 'postEnabled',
            label: 'POST',
            value: true,
          },
          {
            id: 'putEnabled',
            label: 'PUT',
            value: true,
          },
        ],
      },
    ],
    initCustom: ['false'],
    labelGridSize: 4,
  }
  editDialogPropsSetter(editDialogProps)
}

function editEndpointConfiguration(t: TFunction, editDialogPropsSetter: any, setRestProtectionConfigurations: React.Dispatch<any>, row: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('restprotection.editHeader'),
    onSubmit: RESTProtectionConfigurationService.editEndpointConfiguration,
    onSubmitExtraArgs: [t, setRestProtectionConfigurations],
    data: [
      {
        id: 'id',
        type: 'hidden',
        value: row.id,
        render: false,
      },
      {
        id: 'name',
        type: 'text',
        label: t('restprotection.name'),
        value: row.name,
        placeholder: t('restprotection.namePlaceholder'),
        required: true,
      },
      {
        id: 'endpoint',
        type: 'text',
        label: t('restprotection.endpoint'),
        value: row.endpoint,
        placeholder: t('restprotection.endpointPlaceholder'),
        required: true,
      },
      {
        id: 'searchEndpoint',
        type: 'text',
        label: t('restprotection.searchEndpoint'),
        value: row.searchEndpoint,
        required: false,
        placeholder: t('restprotection.searchEndpointPlaceholder'),
      },
      {
        id: 'searchMetadataFields',
        type: 'textarea',
        label: t('restprotection.searchMetadata'),
        value: row.searchMetadataFields,
        required: false,
        placeholder: t('restprotection.searchMetadataPlaceholder'),
      },
      {
        id: 'searchQueryParameter',
        type: 'text',
        label: t('restprotection.searchQueryParameter'),
        value: row.searchQueryParameter,
        required: false,
        placeholder: t('restprotection.searchQueryParameterPlaceHolder'),
      },
      toggleAdditionalEndpointTextBox(row.usesNonGenericTokenProfiles, t, row.endpointsForNonGenericUnprotect),
      {
        id: 'appId',
        type: 'hidden',
        value: row.appId,
      },
      {
        groupLabel: t('restprotection.shouldDetokenizeRequest'),
        groupName: 'detokenizeRequest',
        type: 'switch',
        checkboxes: [
          {
            id: 'detokenizeRequest',
            value: row.detokenizeRequest
          },
        ],
      },
      {
        groupLabel: t('restprotection.shouldEnableFileEncryption'),
        groupName: 'encryptFiles',
        type: 'switch',
        checkboxes: [
          {
            id: 'encryptFiles',
            value: row.encryptFiles
          },
        ],
      },
      {
        id: 'usesNonGenericTokenProfiles',
        type: 'hidden',
        value: row.usesNonGenericTokenProfiles,
      },
      {
        groupLabel: t('restprotection.methods'),
        groupName: 'methods',
        type: 'checkbox',
        render: false,
        checkboxes: [
          {
            id: 'postEnabled',
            label: 'POST',
            value: row.postEnabled,
          },
          {
            id: 'putEnabled',
            label: 'PUT',
            value: row.putEnabled,
          },
        ],
      },
    ],
    labelGridSize: 4,
  }
  editDialogPropsSetter(editDialogProps)
}

function toggleAdditionalEndpointTextBox(custom: boolean, t: TFunction, content?: string): FormDataControl {
  return {
    id: 'endpointsForNonGenericUnprotect',
    type: 'textarea',
    label: t('restprotection.endpointsForNonGenericUnprotect'),
    placeholder: t('restprotection.endpointsForNonGenericUnprotectPlaceholder'),
    required: false,
    value: content,
    render: custom,
  }
}

function deleteEndpointConfiguration(t: Function, confirmDialogPropsSetter: any, setRestProtectionConfigurations: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('restprotection.deleteHeader'),
    body: t('restprotection.deleteBody', { name: row.name }),
    submitText: t('yes'),
    onSubmit: RESTProtectionConfigurationService.deleteEndpointConfiguration,
    onSubmitExtraArgs: [row, t, setRestProtectionConfigurations],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

export default RESTProtectionConfiguration
